import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api, refreshUser } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId || polledRef.current) return;
    polledRef.current = true;
    pollStatus(sessionId, 0);
  }, [sessionId]);

  const pollStatus = async (sid, attempt) => {
    const maxAttempts = 6;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await api.get(`/payments/checkout-status/${sid}`);
      setPaymentData(res.data);

      if (res.data.paymentStatus === 'paid') {
        setStatus('success');
        if (refreshUser) refreshUser();
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setStatus('processing');
      setTimeout(() => pollStatus(sid, attempt + 1), pollInterval);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="p-6 flex items-center justify-center min-h-[500px]" data-testid="payment-success-page">
      <Card className="max-w-md w-full border-0 shadow-lg">
        <CardContent className="p-8 text-center">
          {status === 'checking' || status === 'processing' ? (
            <>
              <Loader2 className="h-16 w-16 text-[#4338CA] animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Processing Payment</h2>
              <p className="text-slate-500">Please wait while we confirm your payment...</p>
            </>
          ) : status === 'success' ? (
            <>
              <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2" data-testid="payment-success-title">Payment Successful!</h2>
              <p className="text-slate-500 mb-6">Your plan is now active. You can start managing shifts immediately.</p>
              <div className="flex gap-3 justify-center">
                <Button
                  className="rounded-full bg-[#4338CA] hover:bg-[#3730A3] text-white"
                  onClick={() => navigate('/manager')}
                  data-testid="go-dashboard-btn"
                >
                  Go to Dashboard <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => navigate('/manager/billing')}
                  data-testid="view-plan-btn"
                >
                  View Plan
                </Button>
              </div>
            </>
          ) : status === 'expired' ? (
            <>
              <XCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Session Expired</h2>
              <p className="text-slate-500 mb-6">The payment session has expired. Please try again.</p>
              <Button
                className="rounded-full bg-[#4338CA] hover:bg-[#3730A3] text-white"
                onClick={() => navigate('/manager/billing')}
              >
                Back to Billing
              </Button>
            </>
          ) : (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Payment Issue</h2>
              <p className="text-slate-500 mb-6">
                {status === 'timeout'
                  ? 'Payment verification timed out. Check your email for confirmation.'
                  : 'There was an issue verifying your payment. Please contact support.'}
              </p>
              <Button
                className="rounded-full bg-[#4338CA] hover:bg-[#3730A3] text-white"
                onClick={() => navigate('/manager/billing')}
              >
                Back to Billing
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
